// src/shared/lib/roles.ts
// Fonte única de verdade para decisões de papel no frontend.
//
// Existiam três vocabulários convivendo (role do backend, role adaptada do
// AuthAdapter e service_roles), com listas divergentes copiadas em vários
// componentes. Estes helpers aceitam qualquer uma dessas formas.

export type BackendRole = 'admin' | 'director' | 'manager' | 'supervisor' | 'employee';

// Normaliza qualquer variante ('Project Manager', 'Manager', 'admin'...) para a
// role canônica do backend.
export function normalizeRole(role?: string | null): BackendRole | null {
  if (!role) return null;
  const r = role.toLowerCase().trim();
  if (r === 'admin' || r === 'administrator') return 'admin';
  if (r === 'director' || r === 'diretor') return 'director';
  if (r === 'project manager' || r === 'manager') return 'manager';
  if (r === 'supervisor') return 'supervisor';
  if (r === 'employee') return 'employee';
  // Fallback tolerante para variantes não previstas
  if (r.includes('admin')) return 'admin';
  if (r.includes('director') || r.includes('diretor')) return 'director';
  if (r.includes('manager')) return 'manager';
  if (r.includes('supervisor')) return 'supervisor';
  return 'employee';
}

const MANAGEMENT: BackendRole[] = ['admin', 'director', 'manager', 'supervisor'];
const PROJECT_MANAGEMENT: BackendRole[] = ['admin', 'director', 'manager'];

// Visão gerencial: enxerga dados da equipe (calendário, plano de entregas, analytics)
export const isManagementRole = (role?: string | null): boolean => {
  const r = normalizeRole(role);
  return !!r && MANAGEMENT.includes(r);
};

// Administra projetos: criar, editar, concluir, excluir
export const canManageProjects = (role?: string | null): boolean => {
  const r = normalizeRole(role);
  return !!r && PROJECT_MANAGEMENT.includes(r);
};

// Aprova horas extras (inclui supervisor, que faz o primeiro nível)
export const canApproveOvertime = (role?: string | null): boolean => isManagementRole(role);

// Administração do sistema (usuários, departamentos)
export const isAdminRole = (role?: string | null): boolean => normalizeRole(role) === 'admin';

export const isEmployeeRole = (role?: string | null): boolean => normalizeRole(role) === 'employee';
